import { Box, Flex } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

type Props = {
  children: React.ReactNode;
};

const FADE_HEIGHT = 48;

export function ActionModeScrollArea({ children }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  };

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const contentEl = contentRef.current;
    if (!scrollEl || !contentEl) return;

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(scrollEl);
    observer.observe(contentEl);
    return () => observer.disconnect();
  }, []);

  return (
    <Box flex={1} pos="relative" style={{ minHeight: 0 }}>
      <Box ref={scrollRef} onScroll={updateScrollState} h="100%" style={{ overflowY: 'auto' }}>
        <Flex ref={contentRef} mih="100%" direction="column" pb="md">
          {children}
        </Flex>
      </Box>
      <ScrollFade position="top" visible={canScrollUp} />
      <ScrollFade position="bottom" visible={canScrollDown} />
    </Box>
  );
}

function ScrollFade({ position, visible }: { position: 'top' | 'bottom'; visible: boolean }) {
  const direction = position === 'top' ? 'to bottom' : 'to top';
  return (
    <Box
      pos="absolute"
      left={0}
      right={0}
      top={position === 'top' ? 0 : undefined}
      bottom={position === 'bottom' ? 0 : undefined}
      h={FADE_HEIGHT}
      style={{
        background: `linear-gradient(${direction}, rgba(255, 119, 28, 0.7), rgba(255, 119, 28, 0))`,
        opacity: visible ? 1 : 0,
        transition: 'opacity 150ms ease',
        pointerEvents: 'none',
      }}
    />
  );
}
